import React, { useState, useEffect, useCallback } from 'react';
import { Database, RefreshCw, AlertTriangle, Check, ArrowRight, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

// Use relative URLs — avoids build-time REACT_APP_BACKEND_URL baking that
// can produce stale URLs in deployed bundles. The rest of the codebase uses
// the same pattern.
async function _fetch(path, opts = {}) {
  const res = await fetch(`/api${path}`, { credentials: 'include', ...opts });
  const body = await res.text();
  let parsed;
  try { parsed = JSON.parse(body); } catch { parsed = { detail: body }; }
  if (!res.ok) throw new Error(parsed.detail || `${res.status}`);
  return parsed;
}

export function CanonicalSyncPanel() {
  const [status, setStatus] = useState(null);
  const [diff, setDiff] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'status' | 'dryrun' | 'commit'
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const loadStatus = useCallback(async () => {
    setBusy('status');
    try {
      const s = await _fetch('/admin/migrate/status');
      setStatus(s);
      // Fetch canonical_data_migration entries from the audit log for visibility
      try {
        const h = await _fetch('/admin/migrate/history?limit=5');
        setHistory(h.entries || []);
      } catch { /* endpoint optional */ }
    } catch (err) {
      toast.error(`Load failed: ${err.message}`);
    } finally {
      setBusy(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runDryRun = async () => {
    setBusy('dryrun');
    setDiff(null);
    try {
      const d = await _fetch('/admin/migrate/canonical-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: true }),
      });
      setDiff(d);
      toast.success('Dry-run complete — review the diff below');
    } catch (err) {
      toast.error(`Dry-run failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const commitMigration = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required for audit trail');
      return;
    }
    setBusy('commit');
    try {
      const r = await _fetch('/admin/migrate/canonical-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true, reason: reason.trim() }),
      });
      toast.success(r.message || 'Migration applied');
      setConfirming(false);
      setReason('');
      setDiff(null);
      await loadStatus();
    } catch (err) {
      toast.error(`Commit failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const needsSync = status && (
    status.rubric_needs_upgrade ||
    status.system_cases_needs_refresh ||
    (status.prompt_versions_upgrade_count || 0) > 0
  );

  return (
    <div className="msml-card" data-testid="canonical-sync-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-[var(--msml-primary)]" />
          <h3 className="text-lg font-medium text-white">Canonical Data Sync</h3>
          {needsSync ? (
            <span className="px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400 text-xs font-medium">
              NEEDS SYNC
            </span>
          ) : status ? (
            <span className="px-2 py-0.5 rounded-full bg-green-900/40 text-green-400 text-xs font-medium">
              IN SYNC
            </span>
          ) : null}
        </div>
        <Button
          variant="ghost" size="sm" onClick={loadStatus}
          disabled={busy === 'status'}
          className="text-[var(--msml-text-muted)]"
          data-testid="canonical-sync-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${busy === 'status' ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      <p className="text-sm text-[var(--msml-text-muted)] mb-4">
        Brings this environment's rubric + system validation cases in line with the current release.
        User accounts, user-created cases, analysis runs, score results, and audit logs are never touched.
        Every migration is audit-logged with reason.
      </p>

      {loading ? (
        <div className="flex justify-center py-6"><span className="msml-spinner" /></div>
      ) : status ? (
        <>
          {/* Status grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-white/10 rounded-lg p-3" data-testid="status-rubric">
              <div className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wide">Rubric version</div>
              <div className="mt-1 flex items-center gap-2">
                <code className={status.rubric_needs_upgrade ? 'text-amber-400' : 'text-green-400'}>
                  {status.current_rubric_version || 'missing'}
                </code>
                {status.rubric_needs_upgrade && (
                  <>
                    <ArrowRight className="w-3 h-3 text-[var(--msml-text-muted)]" />
                    <code className="text-white">{status.target_rubric_version}</code>
                  </>
                )}
              </div>
            </div>
            <div className="border border-white/10 rounded-lg p-3" data-testid="status-system-cases">
              <div className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wide">System validation cases</div>
              <div className="mt-1 flex items-center gap-2">
                <code className={status.system_cases_needs_refresh ? 'text-amber-400' : 'text-green-400'}>
                  {status.system_cases_current}
                </code>
                {status.system_cases_needs_refresh && (
                  <>
                    <ArrowRight className="w-3 h-3 text-[var(--msml-text-muted)]" />
                    <code className="text-white">{status.system_cases_target}</code>
                  </>
                )}
              </div>
            </div>
            <div className="border border-white/10 rounded-lg p-3" data-testid="status-preserved">
              <div className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wide">Preserved (never touched)</div>
              <div className="mt-1 text-sm text-white">
                {status.users_count} users · {status.user_cases_count} user cases · {status.analysis_runs_count} runs
              </div>
            </div>
            <div className="border border-white/10 rounded-lg p-3" data-testid="status-prompt-versions">
              <div className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wide">Prompt versions</div>
              <div className="mt-1 text-sm">
                {(status.prompt_versions_upgrade_count || 0) > 0 ? (
                  <span className="text-amber-400">
                    {status.prompt_versions_upgrade_count} role{status.prompt_versions_upgrade_count === 1 ? '' : 's'} need upgrade:{' '}
                    <code className="text-white">{(status.prompt_versions_need_upgrade || []).join(', ')}</code>
                  </span>
                ) : (
                  <span className="text-green-400">All {(status.prompt_versions_diff || []).length} roles at canonical</span>
                )}
              </div>
            </div>
            <div className="border border-white/10 rounded-lg p-3" data-testid="status-action-needed">
              <div className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wide">Action</div>
              <div className={`mt-1 text-sm ${needsSync ? 'text-amber-400' : 'text-green-400'}`}>
                {needsSync ? 'Run Preview → Apply to bring data up to date' : 'All canonical data is current'}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              size="sm" variant="outline"
              onClick={runDryRun} disabled={busy === 'dryrun'}
              data-testid="canonical-sync-preview"
            >
              {busy === 'dryrun' ? <span className="msml-spinner mr-2" /> : null}
              Preview changes (dry-run)
            </Button>
            <Button
              size="sm"
              onClick={() => setConfirming(true)}
              disabled={!diff || busy === 'commit'}
              data-testid="canonical-sync-apply"
            >
              <Check className="w-4 h-4 mr-1" /> Apply changes
            </Button>
          </div>

          {/* Diff display */}
          {diff && (
            <div className="mt-4 border border-white/10 rounded-lg p-4 bg-black/20" data-testid="canonical-sync-diff">
              <div className="flex items-center gap-2 mb-2 text-sm text-[var(--msml-primary)] font-medium">
                <AlertTriangle className="w-4 h-4" />
                Dry-run result — nothing written yet
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[var(--msml-text-muted)] mb-1">Rubric</div>
                  <code className="text-white">
                    {diff.changed?.rubric?.from_version} → {diff.changed?.rubric?.to_version}
                  </code>
                  <div className="text-[var(--msml-text-muted)] mt-1">
                    {diff.changed?.rubric?.will_update ? 'Will update' : 'No change'}
                  </div>
                </div>
                <div>
                  <div className="text-[var(--msml-text-muted)] mb-1">System cases</div>
                  <code className="text-white">
                    {diff.changed?.cases?.system_cases_before} → {diff.changed?.cases?.system_cases_after_target}
                  </code>
                  <div className="text-[var(--msml-text-muted)] mt-1">
                    {(diff.changed?.cases?.curated_case_ids?.length || 0)} canonical IDs
                  </div>
                </div>
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-[var(--msml-primary)] hover:opacity-80">
                  Curated case IDs ({diff.changed?.cases?.curated_case_ids?.length || 0})
                </summary>
                <div className="mt-2 text-xs font-mono text-[var(--msml-text-secondary)] grid grid-cols-6 gap-1">
                  {(diff.changed?.cases?.curated_case_ids || []).map((id) => (
                    <code key={id} className="bg-black/30 px-2 py-1 rounded">{id}</code>
                  ))}
                </div>
              </details>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-[var(--msml-primary)] hover:opacity-80">
                  Prompt versions diff ({(diff.changed?.prompt_versions?.will_upgrade_roles || []).length} need upgrade)
                </summary>
                <div className="mt-2 text-xs" data-testid="canonical-sync-prompt-diff">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[var(--msml-text-muted)]">
                        <th className="py-1 pr-2">Role</th>
                        <th className="py-1 pr-2">DB</th>
                        <th className="py-1 pr-2">Canonical</th>
                        <th className="py-1">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diff.changed?.prompt_versions?.diff || []).map((r) => (
                        <tr key={r.role} className="border-t border-white/5">
                          <td className="py-1 pr-2 font-mono text-white">{r.role}</td>
                          <td className="py-1 pr-2 font-mono text-[var(--msml-text-secondary)]">{r.db_version || '—'}</td>
                          <td className="py-1 pr-2 font-mono text-white">{r.canonical_version}</td>
                          <td className={`py-1 ${r.needs_upgrade ? 'text-amber-400' : 'text-green-400'}`}>
                            {r.needs_upgrade ? 'will upgrade' : 'ok'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-[var(--msml-primary)] hover:opacity-80">
                  Preserved counts (verified before write)
                </summary>
                <div className="mt-2 text-xs grid grid-cols-2 gap-x-4 gap-y-1 text-[var(--msml-text-secondary)]">
                  {Object.entries(diff.preserved || {}).map(([k, v]) => (
                    <div key={k}><span className="text-[var(--msml-text-muted)]">{k}:</span> {v}</div>
                  ))}
                </div>
              </details>
              {diff.errors?.length > 0 && (
                <div className="mt-3 text-xs text-red-400">
                  <strong>Errors:</strong> {diff.errors.join('; ')}
                </div>
              )}
            </div>
          )}

          {/* Recent migration history */}
          {history.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs text-[var(--msml-primary)] hover:opacity-80"
                       data-testid="canonical-sync-history">
                Recent migrations ({history.length})
              </summary>
              <div className="mt-2 space-y-1">
                {history.map((h, i) => (
                  <div key={i} className="text-xs bg-black/30 rounded px-3 py-2 border border-white/5">
                    <div className="flex items-center justify-between">
                      <span className="text-white">
                        rubric {h.rubric_from} → {h.rubric_to} · cases {h.system_cases_deleted}→{h.system_cases_inserted}
                      </span>
                      <span className="text-[var(--msml-text-muted)] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(h.timestamp)}
                      </span>
                    </div>
                    <div className="mt-1 text-[var(--msml-text-muted)]">
                      by {h.user_email} · env: {h.environment}
                      {h.reason && <> · reason: <em>{h.reason}</em></>}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      ) : null}

      {/* Confirm modal */}
      {confirming && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
             data-testid="canonical-sync-confirm-modal">
          <div className="msml-card max-w-md w-full">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-[var(--msml-warn)] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-medium text-white">Confirm canonical data sync</h4>
                <p className="text-xs text-[var(--msml-text-muted)] mt-1">
                  Rubric <code>{diff?.changed?.rubric?.from_version}</code> → <code>{diff?.changed?.rubric?.to_version}</code>
                  {' · '}
                  System cases <code>{diff?.changed?.cases?.system_cases_before}</code> → <code>{diff?.changed?.cases?.system_cases_after_target}</code>.
                  All user data preserved.
                </p>
              </div>
            </div>
            <label className="block text-xs text-[var(--msml-text-muted)] mb-1">
              Reason (required; recorded in audit log)
            </label>
            <textarea
              className="msml-input w-full min-h-[80px] text-sm"
              placeholder="e.g. Initial prod data catch-up to 1.0.25 / 24 curated cases"
              value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="canonical-sync-reason"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm"
                      onClick={() => { setConfirming(false); setReason(''); }}
                      data-testid="canonical-sync-confirm-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={commitMigration}
                      disabled={busy === 'commit' || !reason.trim()}
                      data-testid="canonical-sync-confirm-apply">
                {busy === 'commit' ? <span className="msml-spinner mr-2" /> : null}
                Apply migration
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
